import React, { useState, useEffect, useCallback } from 'react';
import { File, FileText, FileSpreadsheet, FileType } from 'lucide-react';
import Tippy from '@tippyjs/react';
import { useFileApi } from '../../hooks/useFileApi';
import { showAlert } from '../../utils/sweetAlertUtils';
import ImagePreview from './ImagePreview';
import Loader from './Loader';
import { FileViewer } from './FileViewer';

interface FileIconsProps {
  files: {
    id?: string;
    label: string;
  }[];
  renderSidePanel?: () => React.ReactNode;
}

const FileIcons: React.FC<FileIconsProps> = ({ files, renderSidePanel }) => {
  const [selectedFile, setSelectedFile] = useState<{
    fileName: string;
    url: string;
  } | null>(null);
  const [fileInfoMap, setFileInfoMap] = useState<Record<string, {
    fileName: string;
    url: string;
    contentType: string;
    status: string;
  }>>({});
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});
  const [failedFiles, setFailedFiles] = useState<Record<string, boolean>>({});

  const { getFileUrl } = useFileApi();

  const loadFileInfo = useCallback(async (fileId: string) => {
    // Skip if already loaded, loading, or failed
    if (fileInfoMap[fileId] || loadingFiles[fileId] || failedFiles[fileId]) return;

    setLoadingFiles(prev => ({ ...prev, [fileId]: true }));

    try {
      const response = await getFileUrl(fileId);
      if (response && response.data) {
        setFileInfoMap(prev => ({
          ...prev,
          [fileId]: {
            fileName: response?.data?.fileName || '',
            url: response?.data?.url || '',
            contentType: response?.data?.contentType || 'application/octet-stream',
            status: response?.data?.status || 'Unknown'
          }
        }));
      } else {
        // Mark as failed if no response data
        setFailedFiles(prev => ({ ...prev, [fileId]: true }));
      }
    } catch (error) {
      console.error(`Error loading file info for ${fileId}:`, error);
      // Mark as failed on error
      setFailedFiles(prev => ({ ...prev, [fileId]: true }));
    } finally {
      setLoadingFiles(prev => ({ ...prev, [fileId]: false }));
    }
  }, [fileInfoMap, loadingFiles, failedFiles, getFileUrl]);

  // Load file info on mount for all files
  useEffect(() => {
    files.forEach(file => {
      if (file.id) {
        loadFileInfo(file.id);
      }
    });
    // Only depend on files array, not loadFileInfo to prevent retriggering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleFileClick = async (fileId: string) => {
    // Don't try to load if file previously failed
    if (failedFiles[fileId]) {
      await showAlert({
        title: 'File Unavailable',
        text: 'This file could not be loaded. Please try refreshing the page.',
        icon: 'error'
      });
      return;
    }

    // Always fetch fresh file info to get a new SAS URL (they expire after 10 minutes)
    setLoadingFiles(prev => ({ ...prev, [fileId]: true }));

    try {
      const response = await getFileUrl(fileId);
      if (response && response.data) {
        const fileInfo = {
          fileName: response?.data?.fileName || '',
          url: response?.data?.url || '',
          contentType: response?.data?.contentType || 'application/octet-stream',
          status: response?.data?.status || 'Unknown'
        };

        // Update cache with fresh data
        setFileInfoMap(prev => ({
          ...prev,
          [fileId]: fileInfo
        }));

        if (fileInfo.status !== 'Processed') {
          await showAlert({
            title: 'Almost there...',
            text: `Your file is being processed and checked for security. You'll be able to continue shortly. Please try again in a few seconds.`,
            icon: 'info'
          });
          return;
        }

        setSelectedFile({
          fileName: fileInfo.fileName,
          url: fileInfo.url
        });
      } else {
        // Mark as failed if no response data
        setFailedFiles(prev => ({ ...prev, [fileId]: true }));
        await showAlert({
          title: 'File Unavailable',
          text: 'This file could not be loaded.',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error(`Error loading file info for ${fileId}:`, error);
      setFailedFiles(prev => ({ ...prev, [fileId]: true }));
      await showAlert({
        title: 'File Unavailable',
        text: 'This file could not be loaded.',
        icon: 'error'
      });
    } finally {
      setLoadingFiles(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const getFileIcon = (contentType: string, fileName?: string) => {
    const lowerContentType = contentType.toLowerCase();
    const extension = fileName?.split('.').pop()?.toLowerCase() || '';

    // Check PDF
    if (lowerContentType.includes('pdf') || extension === 'pdf') {
      return <FileText className="w-6 h-6 text-red-500" />;
    }

    // Check Word documents
    if (
      lowerContentType.includes('word') ||
      lowerContentType.includes('msword') ||
      extension === 'doc' ||
      extension === 'docx'
    ) {
      return <FileType className="w-6 h-6 text-blue-500" />;
    }

    // Check Excel spreadsheets
    if (
      lowerContentType.includes('excel') ||
      lowerContentType.includes('spreadsheet') ||
      extension === 'xls' ||
      extension === 'xlsx'
    ) {
      return <FileSpreadsheet className="w-6 h-6 text-green-600" />;
    }

    return <File className="w-6 h-6 text-gray-500" />;
  };

  const renderThumbnail = (fileId: string) => {
    if (loadingFiles[fileId]) {
      return (
        <div className="w-12 h-12 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded">
          <Loader />
        </div>
      );
    }

    const fileInfo = fileInfoMap[fileId];
    const hasFailed = failedFiles[fileId];

    // Show fallback icon if file failed to load
    if (hasFailed || !fileInfo) {
      return (
        <div className="w-12 h-12 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded">
          <FileText className="w-6 h-6 text-gray-400" />
        </div>
      );
    }

    const contentType = fileInfo.contentType;
    const fileName = fileInfo.fileName;

    // Show thumbnail for images
    if (contentType.startsWith('image/')) {
      return <ImagePreview fileId={fileId} size="sm" />;
    }

    // Show PDF icon (not thumbnail to avoid worker errors)
    if (contentType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf')) {
      return (
        <div className="w-12 h-12 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded">
          {getFileIcon(contentType, fileName)}
        </div>
      );
    }

    // Show specific icon for other file types
    return (
      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded">
        {getFileIcon(contentType, fileName)}
      </div>
    );
  };

  const getTooltipContent = (fileId: string, label: string) => {
    const fileInfo = fileInfoMap[fileId];
    const hasFailed = failedFiles[fileId];

    // Show error message for failed files
    if (hasFailed) {
      return (
        <div className="p-2 flex flex-col items-center gap-1">
          <FileText className="w-8 h-8 text-gray-400" />
          <div className="text-xs text-white font-medium">File Unavailable</div>
          <div className="text-xs text-gray-300">{label}</div>
        </div>
      );
    }

    // Show placeholder if still loading or no info yet
    if (!fileInfo) {
      return <span className="text-xs">{label}</span>;
    }

    const contentType = (fileInfo?.contentType || '').toLowerCase();
    const fileName = fileInfo?.fileName || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    const isImage = contentType.startsWith('image/');
    const isPdf = contentType.includes('pdf') || extension === 'pdf';
    const isWord = contentType.includes('word') ||
      contentType.includes('msword') ||
      extension === 'doc' ||
      extension === 'docx';
    const isExcel = contentType.includes('excel') ||
      contentType.includes('spreadsheet') ||
      extension === 'xls' ||
      extension === 'xlsx';

    // For images, show larger preview in tooltip
    if (isImage) {
      return (
        <div className="p-1 min-w-[100px]">
          <div className="flex items-center justify-center min-h-[80px]">
            <ImagePreview fileId={fileId} size="xlg" />
          </div>
          <div className="text-xs text-center mt-1 text-white">
            {label}
          </div>
        </div>
      );
    }

    // For PDFs, show enhanced tooltip (no preview to avoid errors)
    if (isPdf) {
      return (
        <div className="p-2 flex flex-col items-center gap-1">
          <FileText className="w-8 h-8 text-red-500" />
          <div className="text-xs text-white font-medium">PDF Document</div>
          <div className="text-xs text-gray-300">{label}</div>
          <div className="text-xs text-gray-400 italic">Click to view</div>
        </div>
      );
    }

    // For Word documents, show enhanced tooltip
    if (isWord) {
      return (
        <div className="p-2 flex flex-col items-center gap-1">
          <FileType className="w-8 h-8 text-blue-500" />
          <div className="text-xs text-white font-medium">Word Document</div>
          <div className="text-xs text-gray-300">{label}</div>
          <div className="text-xs text-gray-400 italic">Click to view</div>
        </div>
      );
    }

    // For Excel documents, show enhanced tooltip
    if (isExcel) {
      return (
        <div className="p-2 flex flex-col items-center gap-1">
          <FileSpreadsheet className="w-8 h-8 text-green-600" />
          <div className="text-xs text-white font-medium">Excel Spreadsheet</div>
          <div className="text-xs text-gray-300">{label}</div>
          <div className="text-xs text-gray-400 italic">Click to download</div>
        </div>
      );
    }

    // For other files, just show text label
    return <span className="text-xs">{label}</span>;
  };

  if (!files.some(f => f.id)) {
    return <span className="text-gray-500 dark:text-gray-400">None</span>;
  }

  return (
    <div className="flex gap-2">
      {files.map((file, index) =>
        file.id ? (
          <Tippy
            key={index}
            content={getTooltipContent(file.id, file.label)}
            placement="top"
            delay={[300, 0]}
            interactive={fileInfoMap[file.id]?.contentType?.startsWith('image/')}
          >
            <button
              onClick={() => handleFileClick(file.id!)}
              className="hover:opacity-80 transition-opacity cursor-pointer border border-gray-200 dark:border-gray-700 rounded overflow-hidden"
              title={file.label}
            >
              {renderThumbnail(file.id)}
            </button>
          </Tippy>
        ) : null
      )}

      {selectedFile && (
        <FileViewer
          fileName={selectedFile.fileName}
          sasUrl={selectedFile.url}
          onClose={() => setSelectedFile(null)}
          sidePanel={renderSidePanel?.()}
        />
      )}
    </div>
  );
};

export default FileIcons;